import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSellerOrders } from "../hooks/useSellerOrders";
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  Package,
  Clock,
  CheckCircle2,
  Truck,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { OrderStatus } from "../types/order.type";

const STATUS_CONFIG: Record<
  OrderStatus,
  { label: string; color: string; icon: any }
> = {
  PENDING: {
    label: "Pending",
    color: "text-amber-600 bg-amber-50 border-amber-100",
    icon: Clock,
  },
  PROCESSING: {
    label: "Processing",
    color: "text-blue-600 bg-blue-50 border-blue-100",
    icon: Package,
  },
  SHIPPED: {
    label: "Shipped",
    color: "text-indigo-600 bg-indigo-50 border-indigo-100",
    icon: Truck,
  },
  DELIVERED: {
    label: "Delivered",
    color: "text-emerald-600 bg-emerald-50 border-emerald-100",
    icon: CheckCircle2,
  },
  CANCELLED: {
    label: "Cancelled",
    color: "text-rose-600 bg-rose-50 border-rose-100",
    icon: XCircle,
  },
};

const OrdersPage: React.FC = () => {
  const { orders, totalOrders, loading, error, fetchOrders } =
    useSellerOrders();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const limit = 10;

  useEffect(() => {
    fetchOrders({ page, limit, status });
  }, [page, status, fetchOrders]);

  const totalPages = Math.ceil(totalOrders / limit);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50/50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900">
              Manage Orders
            </h1>
            <p className="text-slate-500 mt-1">
              Track and process your customer orders
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search Order ID..."
                className="pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all w-full md:w-64"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="px-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="PROCESSING">Processing</option>
              <option value="SHIPPED">Shipped</option>
              <option value="DELIVERED">Delivered</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-2xl flex items-center gap-3 text-rose-600">
            <AlertCircle size={20} />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100">
                  <th className="py-4 px-6 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                    Order Details
                  </th>
                  <th className="py-4 px-6 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                    Customer ID
                  </th>
                  <th className="py-4 px-6 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                    Items
                  </th>
                  <th className="py-4 px-6 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                    Total Amount
                  </th>
                  <th className="py-4 px-6 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                    Status
                  </th>
                  <th className="py-4 px-6 text-[11px] font-bold text-slate-400 uppercase tracking-widest text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td colSpan={6} className="py-8 px-6">
                        <div className="h-4 bg-slate-100 rounded w-full"></div>
                      </td>
                    </tr>
                  ))
                ) : orders?.length > 0 ? (
                  orders.map((order) => {
                    const statusInfo =
                      STATUS_CONFIG[order.status] || STATUS_CONFIG.PENDING;
                    const StatusIcon = statusInfo.icon;

                    return (
                      <tr
                        key={order.id}
                        className="group hover:bg-slate-50/50 transition-colors"
                      >
                        <td className="py-5 px-6">
                          <div className="flex flex-col">
                            <span className="font-bold text-slate-900 text-sm">
                              #{order.id.slice(0, 8)}...
                            </span>
                            <span className="text-xs text-slate-400 mt-0.5">
                              {formatDate(order.createdAt)}
                            </span>
                          </div>
                        </td>
                        <td className="py-5 px-6">
                          <span className="text-sm text-slate-600 font-medium">
                            #{order.buyerId.slice(0, 8)}...
                          </span>
                        </td>
                        <td className="py-5 px-6">
                          <div className="flex -space-x-2">
                            {order.items.slice(0, 3).map((item, idx) => (
                              <div
                                key={idx}
                                className="w-8 h-8 rounded-lg border-2 border-white bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-600 overflow-hidden"
                              >
                                {item.variant.product.name.charAt(0)}
                              </div>
                            ))}
                            {order.items?.length > 3 && (
                              <div className="w-8 h-8 rounded-lg border-2 border-white bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600">
                                +{order.items?.length - 3}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-5 px-6 text-sm font-bold text-slate-900">
                          ₹{order.totalAmount?.toLocaleString()}
                        </td>
                        <td className="py-5 px-6">
                          <span
                            className={`px-3 py-1 rounded-full text-[11px] font-bold border flex items-center gap-1.5 w-fit ${statusInfo.color}`}
                          >
                            <StatusIcon size={12} />
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="py-5 px-6 text-right">
                          <Link
                            to={`/orders/${order.id}`}
                            className="p-2 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-all inline-flex items-center gap-2 group/btn"
                          >
                            <span className="text-xs font-bold opacity-0 group-hover/btn:opacity-100 transition-opacity">
                              Details
                            </span>
                            <Eye size={18} />
                          </Link>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="py-20 text-center">
                      <div className="flex flex-col items-center justify-center text-slate-400">
                        <Package size={48} strokeWidth={1} />
                        <p className="mt-4 font-medium">No orders found</p>
                        <p className="text-sm mt-1">
                          Try adjusting your filters or check back later
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-6 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Showing{" "}
                <span className="font-bold text-slate-900">
                  {(page - 1) * limit + 1}-{Math.min(page * limit, totalOrders)}
                </span>{" "}
                of{" "}
                <span className="font-bold text-slate-900">{totalOrders}</span>{" "}
                orders
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft size={20} />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`w-10 h-10 rounded-lg text-sm font-bold transition-all ${
                      page === i + 1
                        ? "bg-primary text-white shadow-lg shadow-primary/20"
                        : "bg-white border border-slate-200 text-slate-600 hover:border-primary/50"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg border border-slate-200 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;
